// AICA RefreshIndicator Component
// Unified "last updated X ago" label + refresh IconButton, grouped together.
// Replaces the point implementations that grew independently on
// records/alerts/scripts (L-11): a hardcoded "Updated 2 min ago" string that
// never changed, paired inconsistently with the refresh button (sometimes
// merged into one clickable element, sometimes two separate ones).
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format-relative';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

// Re-render often enough that the relative-time label stays accurate without
// visibly jumping — matches the tick cadence already used for record-list
// relative timestamps elsewhere in the app.
const TICK_INTERVAL_MS = 30_000;

export interface RefreshIndicatorProps {
  /**
   * When the currently-shown data was last fetched — pass the query's
   * `dataUpdatedAt` (epoch ms) directly where a TanStack Query result is
   * available, so the label reflects reality instead of a manually-tracked
   * flag. `null`/`undefined`/`0` renders no label (nothing fetched yet).
   */
  lastUpdated: number | Date | string | null | undefined;
  /** Drives the spin animation and disables the button while a fetch is in flight. */
  isRefreshing: boolean;
  onRefresh: () => void;
  className?: string;
}

function toIsoTimestamp(value: RefreshIndicatorProps['lastUpdated']): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return value > 0 ? new Date(value).toISOString() : null;
  return value;
}

export const RefreshIndicator = memo(function RefreshIndicator({
  lastUpdated,
  isRefreshing,
  onRefresh,
  className,
}: RefreshIndicatorProps) {
  const { t } = useTranslation('common');
  // Forces a re-render on an interval so the relative-time label ("2 min
  // ago" -> "3 min ago") keeps advancing without the caller having to wire
  // up its own ticking clock just for this display.
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), TICK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  const isoTimestamp = toIsoTimestamp(lastUpdated);
  const label = isoTimestamp ? formatRelativeTime(isoTimestamp) : null;

  return (
    <div
      data-testid="refresh-indicator"
      className={cn('flex items-center gap-1.5', className)}
    >
      {label && (
        <span
          data-testid="refresh-indicator-label"
          className="text-xs text-fg-subtle tabular-nums"
        >
          {t('common.updatedRelative', 'Updated {{time}}', { time: label })}
        </span>
      )}
      <button
        type="button"
        data-testid="refresh-indicator-button"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t('common.refresh', 'Refresh')}
        title={t('common.refresh', 'Refresh')}
        className={cn(
          CONTROL_SQUARE.md,
          'rounded flex items-center justify-center text-fg-muted transition-all duration-150 hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
      </button>
    </div>
  );
});
